export type MedicalProfileFileProcessingStatus = 'processing' | 'processed' | 'failed' | 'not-started';

export type MedicalProfileFileFailureType = 'encrypted-pdf' | 'unsupported-content-type' | 'file-too-large';

/**
 * Failure types where re-running the pipeline cannot change the outcome, so the UI must not offer a
 * retry. All are properties of the file itself rather than of the processing attempt.
 */
export const NON_REPROCESSABLE_FAILURE_TYPES: MedicalProfileFileFailureType[] = [
    'encrypted-pdf',
    'unsupported-content-type',
    'file-too-large',
];

export type FileSummaryArtifactType = 'file-summary';

export type DetailedFileSummaryArtifactType = 'detailed-file-summary';

export type TranscriptionResultArtifactType = 'transcription-result';

export type ExternalFileArtifactType = 'ocr-result' | 'page-pdf' | 'image' | 'page-image';

export type PDFFileArtifactType = 'pdf';

export type AudioSourceFile = 'audio-source';

export type OCRSectionArtifactType = 'ocr-section';

export type AudioSectionArtifactType = 'audio-section';

export type TextArtifactType = 'text';

export type PageDimensionArtifactType = 'page-dimensions';

export type PageRotationArtifactType = 'page-rotation';

export type PageCountArtifactType = 'page-count';

export type AudioSpeakersArtifactType = 'audio-speakers';

export type PageDescriptionArtifactType = 'page-description';

export type BoundingBox = {
    width: number;
    height: number;
    left: number;
    top: number;
};

export type OCRSectionArtifactProps = {
    artifactType: OCRSectionArtifactType;
    text: string;
    page: number;
    sectionIndex: number;
    boundingBox: BoundingBox;
};

export type AudioSectionArtifactProps = {
    artifactType: AudioSectionArtifactType;
    speakerIndex: number;
    page: number;
    text: string;
    startTime: number;
    endTime: number;
};

export type AudioSpeaker = {
    speakerIndex: number;
    speakerName: string;
};

export type AudioSpeakersArtifactProps = {
    artifactType: AudioSpeakersArtifactType;
    speakers: AudioSpeaker[];
};

export type FileSummaryArtifactProps = {
    artifactType: FileSummaryArtifactType;
    text: string;
};

export type DetailedFileSummaryArtifactProps = {
    artifactType: DetailedFileSummaryArtifactType;
    text: string;
};

export type ExternalFileArtifactProps = {
    artifactFileId: string;
    page: number;
    artifactType: ExternalFileArtifactType;
};

export type PDFArtifactProps = {
    artifactFileId: string;
    artifactType: PDFFileArtifactType;
};

export type AudioSourceArtifactProps = {
    artifactFileId: string;
    artifactType: AudioSourceFile;
    contentType: string;
};

export type TranscriptionResultArtifactProps = {
    artifactFileId: string;
    artifactType: TranscriptionResultArtifactType;
};

export type TextArtifactProps = {
    artifactType: TextArtifactType;
    text: string;
    page: number;
};

export type PageDimensionArtifactProps = {
    artifactType: PageDimensionArtifactType;
    page: number;
    width: number;
    height: number;
};

export type Rotation = 0 | 90 | 180 | 270;

export type PageRotationArtifactProps = {
    artifactType: PageRotationArtifactType;
    page: number;
    rotation: Rotation;
};

export type PageCountArtifactProps = {
    artifactType: PageCountArtifactType;
    pageCount: number;
};

export type PageDescriptionArtifactProps = {
    artifactType: PageDescriptionArtifactType;
    page: number;
    text: string;
};

export type BaseMedicalProfileArtifactProperties = {
    medicalProfileFileArtifactId: string;
    medicalProfileFileId: string;
    medicalProfileId: string;
};

export type WithBaseMedicalProfileArtifactProperties<T> = T & BaseMedicalProfileArtifactProperties;

export type ArtifactTypeSpecificProps =
    | ExternalFileArtifactProps
    | TextArtifactProps
    | PageDimensionArtifactProps
    | PageRotationArtifactProps
    | FileSummaryArtifactProps
    | OCRSectionArtifactProps
    | PageCountArtifactProps
    | AudioSourceArtifactProps
    | TranscriptionResultArtifactProps
    | DetailedFileSummaryArtifactProps
    | AudioSectionArtifactProps
    | AudioSpeakersArtifactProps
    | PDFArtifactProps
    | PageDescriptionArtifactProps;

export type MedicalProfileFileArtifact = WithBaseMedicalProfileArtifactProperties<ArtifactTypeSpecificProps>;

export type ExternalFileArtifact = WithBaseMedicalProfileArtifactProperties<ExternalFileArtifactProps>;
export type TextArtifact = WithBaseMedicalProfileArtifactProperties<TextArtifactProps>;
export type PageDimensionArtifact = WithBaseMedicalProfileArtifactProperties<PageDimensionArtifactProps>;
export type PageRotationArtifact = WithBaseMedicalProfileArtifactProperties<PageRotationArtifactProps>;
export type FileSummaryArtifact = WithBaseMedicalProfileArtifactProperties<FileSummaryArtifactProps>;
export type OCRSectionArtifact = WithBaseMedicalProfileArtifactProperties<OCRSectionArtifactProps>;
export type PageCountArtifact = WithBaseMedicalProfileArtifactProperties<PageCountArtifactProps>;
export type AudioSourceArtifact = WithBaseMedicalProfileArtifactProperties<AudioSourceArtifactProps>;
export type TranscriptionResultArtifact = WithBaseMedicalProfileArtifactProperties<TranscriptionResultArtifactProps>;
export type DetailedFileSummaryArtifact = WithBaseMedicalProfileArtifactProperties<DetailedFileSummaryArtifactProps>;
export type AudioSectionArtifact = WithBaseMedicalProfileArtifactProperties<AudioSectionArtifactProps>;
export type AudioSpeakersArtifact = WithBaseMedicalProfileArtifactProperties<AudioSpeakersArtifactProps>;
export type PDFFileArtifact = WithBaseMedicalProfileArtifactProperties<PDFArtifactProps>;
export type PageDescriptionArtifact = WithBaseMedicalProfileArtifactProperties<PageDescriptionArtifactProps>;

export type FileProcessingType = 'pdf' | 'image' | 'audio';

export type TranscriptSection = {
    startTime: number;
    endTime: number;
    text: string;
    speakerIndex: number;
    speakerName?: string;
};

export type Transcript = {
    sections: TranscriptSection[];
};
